import React, { useId, useRef } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputBase, Stack, Switch, Typography } from '@mui/material';

import { isEmptyFilterValue } from './filterHelpers';
import { useFilterMenuTokens } from './filterMenuTokens';
import type { FilterFieldDef, FiltersState } from './types';

/** Switch-display booleans toggle inline in this list; they never cascade. */
const isSwitchField = (field: FilterFieldDef): boolean =>
  field.type === 'boolean' && field.display === 'switch';

interface FilterFieldListProps {
  /** Already query-filtered by the popover. */
  fields: FilterFieldDef[];
  value: FiltersState;
  query: string;
  onQueryChange: (query: string) => void;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  /** Field whose editor is currently open — its row stays highlighted. */
  openFieldId: string | null;
  onRowEnter: (fieldId: string, element: HTMLElement) => void;
  onRowLeave: () => void;
  /** Click / Enter / ArrowRight — open the field's editor explicitly. */
  onRowOpen: (fieldId: string, element: HTMLElement) => void;
  /** Click / Enter on a switch-display boolean row. */
  onToggleSwitch: (fieldId: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

/** Root panel content: the "Filter…" search input plus one row per filter type. */
export const FilterFieldList: React.FC<FilterFieldListProps> = ({
  fields,
  value,
  query,
  onQueryChange,
  activeIndex,
  onActiveIndexChange,
  openFieldId,
  onRowEnter,
  onRowLeave,
  onRowOpen,
  onToggleSwitch,
  inputRef,
}) => {
  const tokens = useFilterMenuTokens();
  const listId = useId();
  const rowElements = useRef(new Map<string, HTMLElement>());

  const openActiveField = (viaArrow: boolean) => {
    const field = fields[activeIndex];
    if (!field) return;
    if (isSwitchField(field)) {
      // A switch has no editor to arrow into; only Enter toggles it.
      if (!viaArrow) onToggleSwitch(field.id);
      return;
    }
    const element = rowElements.current.get(field.id);
    if (element) onRowOpen(field.id, element);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && fields.length > 0) {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      onActiveIndexChange((activeIndex + delta + fields.length) % fields.length);
      return;
    }
    if (event.key === 'Enter' || event.key === 'ArrowRight') {
      event.preventDefault();
      openActiveField(event.key === 'ArrowRight');
    }
  };

  const activeField = fields[activeIndex];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          borderBottom: `1px solid ${tokens.line}`,
          height: 36,
          px: 1.5,
        }}
      >
        <SearchIcon sx={{ color: tokens.faint, fontSize: 16 }} />
        <InputBase
          autoFocus
          fullWidth
          inputRef={inputRef}
          placeholder="Filter…"
          value={query}
          onChange={event => onQueryChange(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          inputProps={{
            'aria-label': 'Search filters',
            'aria-activedescendant': activeField
              ? `${listId}-${activeField.id}`
              : undefined,
            'aria-controls': listId,
          }}
          sx={{ fontSize: tokens.fontSize }}
        />
      </Stack>

      <Box
        id={listId}
        role="listbox"
        aria-label="Filter types"
        sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}
      >
        {fields.map((field, index) => {
          const appliedValue = value[field.id];
          const isApplied = appliedValue !== undefined && !isEmptyFilterValue(appliedValue);
          const isOpen = field.id === openFieldId;
          const isActive = index === activeIndex;
          const isSwitch = isSwitchField(field);
          return (
            <Stack
              direction="row"
              key={field.id}
              id={`${listId}-${field.id}`}
              role="option"
              aria-selected={isOpen}
              ref={(element: HTMLElement | null) => {
                if (element) rowElements.current.set(field.id, element);
                else rowElements.current.delete(field.id);
              }}
              onMouseEnter={event => {
                onActiveIndexChange(index);
                if (!isSwitch) onRowEnter(field.id, event.currentTarget);
              }}
              onMouseLeave={isSwitch ? undefined : onRowLeave}
              onClick={event =>
                isSwitch
                  ? onToggleSwitch(field.id)
                  : onRowOpen(field.id, event.currentTarget)
              }
              sx={{
                alignItems: 'center',
                bgcolor: isOpen ? tokens.selectedBg : isActive ? tokens.hoverBg : undefined,
                cursor: 'pointer',
                gap: 1,
                height: tokens.rowHeight,
                px: 1.5,
              }}
            >
              {field.icon && (
                <Box
                  sx={{
                    alignItems: 'center',
                    color: tokens.dim,
                    display: 'flex',
                    fontSize: 16,
                    width: 18,
                  }}
                >
                  {field.icon}
                </Box>
              )}
              <Typography noWrap sx={{ color: tokens.ink, fontSize: tokens.fontSize }}>
                {field.label}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {isSwitch ? (
                // Visual-only: the row is the interactive element.
                <Switch
                  checked={appliedValue?.kind === 'boolean' && appliedValue.value}
                  size="small"
                  sx={{ pointerEvents: 'none' }}
                  slotProps={{ input: { 'aria-hidden': true, tabIndex: -1 } }}
                />
              ) : (
                <>
                  {isApplied &&
                    (appliedValue.kind === 'multiSelect' ? (
                      <Typography
                        sx={{ color: tokens.dim, fontSize: tokens.metaFontSize }}
                      >
                        {appliedValue.values.length}
                      </Typography>
                    ) : (
                      <CheckIcon sx={{ color: tokens.dim, fontSize: 14 }} />
                    ))}
                  <ChevronRightIcon sx={{ color: tokens.faint, fontSize: 16 }} />
                </>
              )}
            </Stack>
          );
        })}
        {fields.length === 0 && (
          <Typography sx={{ color: tokens.dim, fontSize: tokens.fontSize, px: 1.5, py: 2 }}>
            No matching filters.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
